import { documentoService } from '@/services/documentoService'
import { oportunidadeService } from '@/services/oportunidadeService'
import { clienteService } from '@/services/clienteService'
import { TIPO_ENTIDADE_OPORTUNIDADE } from '@/utils/docsObrigatorios'
import { Documento } from '@/types/documento'
import { Oportunidade } from '@/types/oportunidade'
import { Cliente } from '@/types/cliente'
import { PageResponse } from '@/types/comum'

export interface ReservaOption {
  id: string
  label: string
}

/**
 * Monta as opções do filtro de reserva no formato "Empresa — Produto".
 * Pura (sem I/O) para ser testável de forma isolada.
 */
export function montarReservaOptions(
  oportunidades: Oportunidade[],
  clientes: Cliente[]
): ReservaOption[] {
  const clienteById = new Map(clientes.map((c) => [c.id, c]))
  return oportunidades.map((o) => {
    const cliente = clienteById.get(o.clienteId as unknown as string)
    const empresa =
      cliente?.razaoSocial ?? `Cliente ${String(o.clienteId).slice(0, 8)}…`
    return { id: o.id, label: `${empresa} — ${o.tipoNecessidade ?? '—'}` }
  })
}

/**
 * Busca a página de documentos respeitando o filtro de reserva.
 *
 * - Com `reservaId`: filtra no BACKEND por (OPORTUNIDADE, reservaId) — escala
 *   bem mesmo com muitos documentos no sistema.
 * - Sem `reservaId`: listagem geral paginada (comportamento padrão).
 */
export function buscarDocumentosPage(
  reservaId: string | null
): Promise<PageResponse<Documento>> {
  return reservaId
    ? documentoService.listarFiltrado(TIPO_ENTIDADE_OPORTUNIDADE, reservaId, 0, 500)
    : documentoService.listar(0, 100)
}

/**
 * Envia um ou mais arquivos vinculando-os a uma reserva (oportunidade).
 * O vínculo é obrigatório: o chamador deve garantir um `reservaId` válido.
 * Sem um tipo específico, documentos avulsos entram como 'OUTRO'.
 */
export async function enviarDocumentosParaReserva(
  files: File[],
  reservaId: string,
  tipo: string = 'OUTRO'
): Promise<void> {
  for (const file of files) {
    await documentoService.upload(file, tipo, reservaId, TIPO_ENTIDADE_OPORTUNIDADE)
  }
}

/** Carrega as reservas (oportunidades + clientes) e devolve as opções do filtro. */
export async function carregarReservaOptions(): Promise<ReservaOption[]> {
  const [opsPage, clientesPage] = await Promise.all([
    oportunidadeService.listar(0, 200),
    clienteService.listar(0, 500),
  ])
  return montarReservaOptions(opsPage.content ?? [], clientesPage.content ?? [])
}
